import { Component, OnInit, ViewChild, ElementRef, Renderer2, OnDestroy, ChangeDetectionStrategy, AfterViewInit } from '@angular/core';
import { LayoutThemeEnum } from 'src/app/layout/models/layout-theme.enum';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { LayoutState } from 'src/app/layout/state/layout.state';
import { Observable, merge, Subject, combineLatest, of, fromEvent } from 'rxjs';
import { EditViewEnum } from '../../models/edit-view.enum';
import { SetView, ShowColumn, ResetEditPage, SetObjectVisibility } from '../../state/edit.actions';
import { EditState } from '../../state/edit.state';
import { EngineState } from 'src/app/engine/state/engine.state';
import { StoryState } from 'src/app/story/state/story.state';
import { StoryModel } from 'src/app/story/models/story.model';
import { filter, delay, map, take, switchMap, takeUntil, flatMap, first } from 'rxjs/operators';
import { 
  LoadStory, 
  SetIncomingStory, 
  BuildStory, 
  LoadStorySuccess, 
  LoadStoryFail, 
  SetStoryText, 
  SetStory, 
  SetStoryId, 
  ImportStorySuccess} from 'src/app/story/state/story.actions';
import { 
  SetEnginePosition,
  ClickNode,
  SetActiveTool,
  SetDisplay,
  ToggleLabels} from 'src/app/engine/state/engine.actions';
import { EnginePositionModel } from 'src/app/engine/models/engine-position.model';
import { SetTheme } from 'src/app/layout/state/layout.actions';
import { EngineVisibilityEnum } from 'src/app/engine/models/engine-visibility.enum';
import { NavigationStart, ActivatedRoute, Router } from '@angular/router';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { ViewTreeNode } from '../../../engine/models/view.tree-node';
import { BdGraph } from 'src/lib/graph/graph.model';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { 
  PreviousChapter,
  NextChapter,
  DuplicateChapter,
  RecordChapterVisibilityState, 
  RecordChapterCamera,
  SelectChapter,
  LastChapter,
  RecordChapterPaintState,
  RemoveChapter,
  SetChapters,
  SetChapterBackground,
  SetAllChaptersBackground} from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { MatDialog, MatSnackBar } from '@angular/material';
import { SetCamera, LockCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { SaveBookmark, SelectBookmark, SaveBookmarkSuccess, SaveBookmarkFail, SetBookmarkText } from 'src/app/bookmarks/state/bookmarks.actions';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { PublishDialogComponent, PublishDialogData } from 'src/app/publish/components/publish-dialog/publish-dialog.component';
import { AnimationService } from 'src/app/engine/animation.service';
import { 
  RemoveLabel,
  EnableContextLabel,
  ConvertLabels,
  AddLabel,
  CopyLabels,
  RemoveAllLabels,
  SetLabel } from 'src/app/story/state/labels/story.labels.actions';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { BookmarksService } from 'src/app/bookmarks/bookmarks.service';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { UserProfileModel } from 'src/app/auth/models/user-profile.model';
import { Location } from '@angular/common';
import { ContentDialogComponent } from '../../components/content-dialog/content-dialog.component';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { Undo, Redo, ResetHistory } from 'src/app/history/state/history.actions';
import { HistoryService } from 'src/app/history/history.service';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { SetCheckedNodes, ToggleVisibleNodes, CheckNode, SelectNode, ExpandNode, ResetEngineNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { ToolIcons } from 'src/app/engine/models/tool-icons.const';

import * as Mousetrap from 'mousetrap';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { CameraService } from 'src/app/engine/camera.service';
import { PreviousRouteService } from 'src/app/shared/previous-route.service';
import { LabelRecipe } from 'src/app/story/models/labels/label-recipe.enum';

import { environment } from 'src/environments/environment';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { SaveModuleSuccess } from 'src/app/modules/state/modules.actions';

import { StoryLoadTypeEnum } from 'src/app/story/models/story-load-type.enum';
import { EditStoryComponent } from '../../edit-changes.guard';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { AuthService } from 'src/app/auth/auth.service';
import { UserMessageEnum } from 'src/app/auth/models/user-message-model';
import { SetUserMessageViewed, GetUserMessage, GetUserMessageSuccess, LogoutSuccess } from 'src/app/auth/state/auth/auth.actions';
import { StoryService } from 'src/app/story/story.service';

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditComponent implements OnInit, OnDestroy, AfterViewInit, EditStoryComponent {
  // Local declarations so we can use them in the template
  layoutThemeEnum = LayoutThemeEnum;
  editViewEnum = EditViewEnum;
  toolEnum = ToolEnum;
  toolIcons = ToolIcons;

  exportType = environment.exportType; // 'module' or 'bookmark'

  // track changes: requiring save
  isStoryDirty = false;
  
  @ViewChild('engineContainer')
  engineContainer: ElementRef;

  /**
   * Selections
   */

  // Layout
  @Select(LayoutState.getTheme)
  theme$: Observable<LayoutThemeEnum>;

  // Edit
  @Select(EditState.getView)
  view$: Observable<EditViewEnum>;

  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(EditState.getShowColumns)
  showColumns$: Observable<boolean>;

  @Select(EditState.getShowLeftColumn)
  showLeftColumn$: Observable<boolean>;

  @Select(EditState.getShowRightColumn)
  showRightColumn$: Observable<boolean>;

  @Select(EditState.getShowHeader)
  showHeader$: Observable<boolean>;

  @Select(EditState.getShowFooter)
  showFooter$: Observable<boolean>;

  @Select(EditState.getRecord)
  record$: Observable<boolean>;

  // Engine
  @Select(EngineState.getReady)
  engineReady$: Observable<boolean>;

  // Engine -- Per-chapter customization of the scene-graph to display to user
  @Select(EngineState.getChapter)
  chapter$: Observable<string>;

  @Select(EngineNodesState.getHoveredNode)
  hoveredNode$: Observable<ViewTreeNode>;

  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<ViewTreeNode>;

  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  // Camera
  @Select(EngineCameraState.getStoredCamera('__copy'))
  cameraCopy$: Observable<CameraModel>;

  // Story
  @Select(StoryState.getStory)
  story$: Observable<StoryModel>;

  @Select(StoryState.getIncomingStoryInfo)
  incoming$: Observable<{id: string, type: StoryType}>;

  // Story Labels
  @Select(StoryLabelsState.getLabels)
  labels$: Observable<LabelModel[]>;

  @Select(StoryLabelsState.getSelectedLabel)
  selectedLabel$: Observable<LabelModel[]>;

  @Select(StoryChaptersState.getChaptersLoaded)
  chaptersLoaded$: Observable<any>;

  @Select(StoryChaptersState.getAllChaptersReady)
  allChaptersReady$: Observable<boolean>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(BookmarksState.getSelectedBookmark)
  selectedBookmark$: Observable<BookmarkModel>;

  @Select(BookmarksState.getSaving)
  saving$: Observable<boolean>;

  // User Profile
  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  navigationStart$: Observable<NavigationStart>;
  windowResize$: Observable<any>;

  private ngUnsubscribe = new Subject();

  id: string;
  type: StoryType;
  isAnimated = false;
  atBookmarkLimit = false;
  record: boolean;

  constructor(
    private actions: Actions,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private animationService: AnimationService,
    private bookmarkService: BookmarksService,
    private historyService: HistoryService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private graphService: BdGraphService,
    private location: Location,
    private snackBar: MatSnackBar,
    private store: Store,
    private storyService: StoryService,
    private snackbar: MatSnackBar
  ) { }

  ngOnInit() {
    const { id } = this.activatedRoute.snapshot.queryParams;
    this.id = this.sanitizeRouteId(id);
    this.type = this.storyService.getStoryType(this.id);

    this.store.dispatch(new SetTheme(LayoutThemeEnum.Dark));
    this.store.dispatch(new SetActiveTool(ToolEnum.Select));
    this.store.dispatch(new ResetEditPage());
    this.store.dispatch(new SetDisplay(true));
    this.store.dispatch(new ToggleLabels(true));
    this.store.dispatch(new LockCamera(true));
    this.store.dispatch(new EnableContextLabel(false));
    this.store.dispatch(new SetIncomingStory(this.id, this.type));
    // set story metadata
    this.store.dispatch(new SelectBookmark(this.id, this.type));
    
    this.resizeEngineContainer();
    this.initListeners();
    this.initAuthChecks();
    this.initKeyboardListeners();
    this.initDirtyCheck();
  }

  ngAfterViewInit() {
    setTimeout(() => this.resizeEngineContainer(), 0); 
  }

  ngOnDestroy() {
    // #TODO: Put theme management in route
    // Change the theme
    this.store.dispatch(new SetTheme(LayoutThemeEnum.Light));
    this.store.dispatch(new SetDisplay(false));
    this.store.dispatch(new ResetEngineNodes());
    this.store.dispatch(new EnableContextLabel(false));
    this.unbindKeyboardListeners();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // confirm if save needed before exiting
  canDeactivate(): Observable<boolean> {
    // validate session
    const profile = this.store.selectSnapshot(AuthState.getUserProfile);

    if (profile && this.isStoryDirty) { // ask save before exiting
      const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
      const bookmarkTitle = this.store.selectSnapshot(StoryState.getStory).displayName;
      const saveType = this.getBookmarkSaveType(bookmark);

      return this.confirmSaveDialog(true) // confirm + save (optionally)
        .pipe(
          flatMap((confirmSave) => {
            if (typeof (confirmSave) === 'undefined') { // navigation: cancel
              return of(false);
            } else if (confirmSave) { // navigation: save + leave
              const savedBookmark$ = saveType === 'save' ? this.saveBookmark(bookmark, true) : this.saveNewBookmarkDialog('Save As', bookmarkTitle);
              return savedBookmark$
                .pipe(map(() => true));
            } else { // navigation: leave
              return of(true);
            }
          }),
          takeUntil(this.ngUnsubscribe)
        );

    } else { // exit as normal
      return of(true);
    }
  }

  resizeEngineContainer() {
    const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = this.engineContainer.nativeElement;
    const position = new EnginePositionModel(offsetLeft, offsetTop, offsetWidth, offsetHeight, EngineVisibilityEnum.Shown);
    this.store.dispatch(new SetEnginePosition(position));
  }

  /**
   * Listeners
   */
  initListeners() {
    this.windowResize$ = fromEvent(window, 'resize');

    combineLatest(this.engineReady$, this.incoming$).pipe(
      filter(([ready, incoming]) => ready && !!incoming.id),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.store.dispatch( new SelectChapter()); // null chapter state
      this.loadStory();
    });

    // Showing colulmns has changed, let the 3D canvas know
    // Pipe through a delay so that element in window has a chance to redraw
    merge(this.showLeftColumn$, this.showRightColumn$, this.showHeader$, this.showFooter$, this.windowResize$)
      .pipe(
        delay(10),
        takeUntil(this.ngUnsubscribe)
      ).subscribe(() => {
        this.resizeEngineContainer();
      });

    // Listen for changes to record state
    this.record$.subscribe((record) => {
      this.record = record;
    });

    // Listen for different chapter. Use id explicitly, because changes to chapter can occur within a chapter.
    this.selectedChapterId$
      .pipe(
        filter(chapter => chapter != null),
        map(_ => this.store.selectSnapshot(StoryChaptersState.getSelectedChapter)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(chapter => {
        this.handleChapterChange(chapter);
      });

    // Listen for selected label
    this.selectedLabel$
      .pipe(
        filter(label => label != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new SetActiveTool(ToolEnum.Label));
        this.store.dispatch(new SetView(EditViewEnum.Label));
      });

    // Convert annotation labels to StudioNext labels, to push users towards a newer style when using human studio.
    if (this.type === StoryType.Module) {
      this.labels$
        .pipe(
          filter(labels => labels.length > 0),
          filter(() => environment.exportType === 'bookmark'),
          take(1),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(labels => {
          setTimeout(() => {
            // In the case of internal, labels may have been converted already. Apply this filter to ensure we only
            // convert labels that are not the StudioNext style.
            labels = labels.filter(label => {
              return label.theme !== LabelThemeEnum.StudioNext;
            });

            this.store.dispatch(new ConvertLabels(labels, LabelRecipe.StudioNext));
          });
        });
    }
     
    // Listen for actions
    this.actions
      .pipe(
        ofActionDispatched(ClickNode),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action) => {
        this.handleNodeClick(action.node, action.event);
      });

    this.actions
      .pipe(
        ofActionDispatched(SaveBookmarkSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action) => this.handleSaveBookmarkSuccess(action));

    this.actions
      .pipe(
        ofActionDispatched(SaveBookmarkFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action) => this.handleSaveBookmarkFailure(action));

    this.actions
      .pipe(
        ofActionDispatched(SaveModuleSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action) => this.handleSaveModuleSuccess(action));
    
    this.actions
      .pipe(
        ofActionDispatched(DuplicateChapter),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.store.dispatch(new LastChapter());
        });
      });

    this.actions
      .pipe(
        ofActionDispatched(ToggleVisibleNodes),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: ToggleVisibleNodes) => {
        // Record with current chapter
        const record = this.store.selectSnapshot(EditState.getRecord);
        this.store.dispatch(new RecordChapterVisibilityState(action.nodes, action.visible, record));

        if (action.recordHistory) {
          this.historyService.recordViewGraphHistory();
        }
      });

    // content not found
    this.actions
      .pipe(
        ofActionDispatched(LoadStoryFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new SetIncomingStory(null, null));
        
        this.router.navigate(['/404'], {
          queryParams: {
            errorType: 'content',
            errorUrl: this.router.url
          },
          replaceUrl: true
        });
      });

    // NPS survey
    this.actions
      .pipe(
        ofActionDispatched(SaveBookmarkSuccess),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new GetUserMessage(UserMessageEnum.SurveyMonkey, 'nps_v4'));
      });

    this.actions
      .pipe(
        ofActionDispatched(GetUserMessageSuccess),
        filter((event: GetUserMessageSuccess) => event.message.messageType === UserMessageEnum.SurveyMonkey),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((event: GetUserMessageSuccess) => {
        const { showUserAlert, messageData, messageSeenId } = event.message;
        if (showUserAlert && messageData) {
          this.store.dispatch(new SetUserMessageViewed(messageSeenId));
          this.dialogService.openNpsSurveyDialog(messageData);
        }
      });

    // Listen for story import
    this.actions
      .pipe(
        ofActionDispatched(ImportStorySuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: ImportStorySuccess) => {
        this.snackbar.open(`Successfully imported ${action.story.displayName}.`);
      });
  }

  // #TODO: [EM] #SceneEditorTouchpoint - assumes bookmark  service authorizes editability
  initAuthChecks() {
    // Listen to bookmark loaded, check for access
    this.selectedBookmark$
      .pipe(
        filter(_ => this.type === 'module'),
        filter(bookmark => bookmark != null),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((bookmark) => {
        if (this.type === StoryType.Bookmark && !this.bookmarkService.canEdit(bookmark)) {
          this.openBookmarkAccessDialog();
        }
      });

    // validate bookmark limit if cannot overwrite selected bookmark
    this.selectedBookmark$
      .pipe(
        filter((selectedBookmark) => !!selectedBookmark),
        filter((selectedBookmark) => !this.isBookmarkEditable(selectedBookmark)),
        switchMap(() => {
          const profile = this.store.selectSnapshot(AuthState.getUserProfile);
          if (profile && profile.premium_account_valid) {
            return of(false);
          } else {
            return this.bookmarkService.atMaxFreeBookmarkCount();
          }
        }),
        takeUntil(this.ngUnsubscribe))
      .subscribe((hasExceeded) => {
        this.handleBookmarkLimit(hasExceeded);
      });

    // validate can view premium content
    this.selectedBookmark$
      .pipe(
        filter((selectedBookmark) => !!selectedBookmark),
        takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handlePremiumContent();
      });
  }

  
  initDirtyCheck() {
    // mark not modified
    this.actions
      .pipe(
        ofActionDispatched(LoadStorySuccess, SaveBookmarkSuccess, LogoutSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.isStoryDirty = false;
      });

    // mark modified
    this.actions
      .pipe(
        ofActionDispatched(
          // node selections
          ToggleVisibleNodes,
          RecordChapterCamera,
          RecordChapterPaintState,
          RecordChapterVisibilityState,
          // labels
          AddLabel,
          RemoveLabel,
          RemoveAllLabels,
          CopyLabels,
          SetLabel,
          // chapters
          DuplicateChapter,
          RemoveChapter,
          ImportStorySuccess,
          // info text
          SetBookmarkText,
          SetStoryText,
          SetChapterBackground,
          SetAllChaptersBackground
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.isStoryDirty = true;
      });

    // special case: editing chapter info after loaded
    this.actions.pipe(ofActionDispatched(SetChapters))
      .pipe(
        filter(() => this.store.selectSnapshot(StoryChaptersState.getAllChaptersReady)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.isStoryDirty = true;
      });
  }

  handleBookmarkLimit(exceeded: boolean) {
    this.atBookmarkLimit = exceeded;

    const profile = this.store.selectSnapshot(AuthState.getUserProfile);
    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);

    if (profile && bookmark) {
      const isPremiumUser = profile.premium_account_valid;
      const isBookmarkEditable = this.isBookmarkEditable(bookmark);

      // bookmark limit
      if (exceeded && !isBookmarkEditable && !isPremiumUser) {
        const dialog$ = this.dialogService.openLimitDialog();

        dialog$.afterClosed()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(result => {
            if (result) {
              this.dialogService.openUpgradeDialog();
            }
          });
      }
    }
  }

  handlePremiumContent() {
    const profile = this.store.selectSnapshot(AuthState.getUserProfile);
    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
    if (profile && bookmark) {
      if (bookmark.premium && !profile.premium_account_valid) {
        const dialogRef = this.dialogService.openPremiumContentDialog();
        dialogRef.afterClosed()
          .pipe(
            takeUntil(this.ngUnsubscribe)
          )
          .subscribe(confirmed => {
            if (confirmed) {
              this.dialogService.openUpgradeDialog();
            }
            // always redirect
            this.router.navigate(['/dashboard']);
          });
      }
    }
  }

  initKeyboardListeners() {
    // Tool switching
    Mousetrap.bind('1', () => this.handleSelectTool(ToolEnum.Select));
    Mousetrap.bind('2', () => this.handleSelectTool(ToolEnum.Hide));
    Mousetrap.bind('3', () => this.handleSelectTool(ToolEnum.Label));
    Mousetrap.bind('4', () => this.handleSelectTool(ToolEnum.Paint));
    Mousetrap.bind('5', () => this.handleSelectTool(ToolEnum.Position));

    // Column toggles
    Mousetrap.bind('meta+,', (event) => {
      event.preventDefault();
      this.showColumn('left');
    });

    Mousetrap.bind('meta+.', (event) => {    
      event.preventDefault();
      this.showColumn('right');
    });

    Mousetrap.bind('meta+/', (event) => {    
      event.preventDefault();
      this.showColumn('header');
    });

    Mousetrap.bind('meta+m', (event) => {
      event.preventDefault();
      this.showColumn('footer');
    });

    Mousetrap.bind('meta+z', (event) => {
      event.preventDefault();
      this.undo();
    });

    Mousetrap.bind('meta+y', (event) => {
      event.preventDefault();
      this.redo();
    });

    // Chapter browser
    Mousetrap.bind('.', () => this.nextChapter());
    Mousetrap.bind(',', () => this.previousChapter());

    // Delete
    Mousetrap.bind(['del', 'backspace'], () => this.handleDelete());
    
    // Select all
    Mousetrap.bind('meta+a', (event) => {
      event.preventDefault();
      this.handleSelectAll();
    });
  }

  unbindKeyboardListeners() {
    Mousetrap.unbind(['1', '2', '3', '4', '5', 'meta+m', 'meta+,', 'meta+.', 'meta+/', '.', ',', 'del', 'backspace', 'meta+a']);
  }

  /**
   * Handlers
   */  
  handleChapterTitleClick() {
    this.openChapterContentDialog();
  }

  handleChapterChange(chapter: ChapterModel) {
    this.store.dispatch(new SetCamera(chapter.camera.location));
    this.isAnimated = this.animationService.isChapterAnimated(chapter);

    this.viewGraph$
      .pipe(
        take(1),
        delay(1)
      ).subscribe(() => {
        this.resetHistory();
      });
  }

  handleSelectTool(tool: ToolEnum) {
    this.store.dispatch(new SetActiveTool(tool));

    switch (tool) {
      case ToolEnum.Paint:
        this.store.dispatch(new SetView(EditViewEnum.Paint));
        break;
      
      case ToolEnum.Label:
        this.store.dispatch(new SetView(EditViewEnum.Label));
        break;

      case ToolEnum.Position:
        this.store.dispatch(new SetView(EditViewEnum.Position));
        break;
    }
  }

  handleToggleVisibleNodes(event: { nodes: ViewTreeNode[], visible?: boolean }) {
    this.store.dispatch(new ToggleVisibleNodes(event.nodes, event.visible));

    const record = this.store.selectSnapshot(EditState.getRecord);
    this.store.dispatch(new RecordChapterVisibilityState(event.nodes, event.visible, record));
    this.store.dispatch(new SetCheckedNodes([]));
  }
  
  handleShowColumnClick(column: 'left' | 'right') {
    this.showColumn(column);
  }

  handleUndoClick() {
    this.undo();
  }

  handleRedoClick() {
    this.redo();
  }

  handleDelete() {
    const activeLabel = this.store.selectSnapshot(StoryLabelsState.getSelectedLabel);
    const selectedNodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);

    if (activeLabel) {
      this.store.dispatch(new RemoveLabel(activeLabel));
    } else if (selectedNodes.length) {
      this.store.dispatch(new SetObjectVisibility(selectedNodes, false));
    }
  }

  handleSelectAll() {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const rootNodeIds = GraphUtils.getRoots(viewGraph);
    const rootNodes = this.graphService.getNodes(viewGraph, rootNodeIds);

    this.store.dispatch(new SetCheckedNodes(rootNodes));
  }

  /**
   * Dispatchers
   */
  loadStory() {    
    const {id, type} = this.store.selectSnapshot(StoryState.getIncomingStoryInfo);
    this.store.dispatch(new LoadStory(id, type));
  }

  handleNodeClick(node: ViewTreeNode, event: HammerInput) {
    const tool = this.store.selectSnapshot(EngineState.getActiveTool);
    
    if (!node && !event.srcEvent.shiftKey) {
      this.store.dispatch(new SetCheckedNodes([]));
      return;
    }

    if (event.srcEvent.shiftKey) {
      const checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).some(value => value.id === node.id);

      this.store.dispatch(new CheckNode(node, !checked));
      this.store.dispatch(new SelectNode(node));
    } else {
      switch (tool) {
        case ToolEnum.Hide:
          this.store.dispatch(new SetObjectVisibility([node], false));
          break;
        case ToolEnum.Select:
        case ToolEnum.Position:
          const checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes).some(value => value.id === node.id);
          
          (checked) ? 
            this.store.dispatch(new SetCheckedNodes([])) :
            this.store.dispatch(new SetCheckedNodes([node]));
            
          this.store.dispatch(new SelectNode(node));
          this.store.dispatch(new ExpandNode(node));
          break;
      }
    }
  }

  showColumn(column: 'left' | 'right' | 'header' | 'footer') {
    let show = true;

    switch (column) {
      case 'left':
        show = !this.store.selectSnapshot(EditState.getShowLeftColumn);
        break;
      case 'right':
        show = !this.store.selectSnapshot(EditState.getShowRightColumn);
        break;
      case 'header':
        show = !this.store.selectSnapshot(EditState.getShowHeader);
        break;
      case 'footer':
        show = !this.store.selectSnapshot(EditState.getShowFooter);
        break;
    }

    this.store.dispatch(new ShowColumn(column, show));
  }
    
  handleSaveBookmark(): void {
    if (this.atBookmarkLimit) {
      this.upgrade();
    } else {
      const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
      const saveType = this.getBookmarkSaveType(bookmark);
  
      switch (saveType) {
        case 'save':
          this.saveBookmark(bookmark)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => { });
          break;
        case 'saveAs':
          this.saveBookmarkAs();
          break;
      }
    }
  }

  // Save over loaded module
  saveModule(): void {
    this.dialogService.openSaveModuleDialog(this.id);
  }

  // #TODO: [EM] #SceneEditorTouchpoint - Assumes the destination is a bookmark
  saveBookmarkAs(): void {
    if (this.atBookmarkLimit) {
      return;
    }

    const title = 'Save As';
    const bookmarkTitle = this.store.selectSnapshot(StoryState.getStory).displayName;

    this.saveNewBookmarkDialog(title, bookmarkTitle)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {});
  }

  publishBookmark() {
    if (this.atBookmarkLimit) {
      return;
    }

    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
    const saveType = this.getBookmarkSaveType(bookmark);

    // preview saved bookmark
    if (saveType === 'save' && bookmark) {

      if (this.isStoryDirty) { // save + preview
        this.confirmSaveDialog()
          .pipe(
            flatMap((confirmed) => confirmed ? this.saveBookmark(bookmark, true) : of(bookmark)), // save if confirmed
            takeUntil(this.ngUnsubscribe)
          )
          .subscribe((previewBookmark) => { // continue to preview
            this.previewBookmark(previewBookmark);
          });
      } else {
        this.previewBookmark(bookmark);
      }

    } else if (saveType === 'saveAs') { // save, then preview

      const title = 'Save As';
      const bookmarkTitle = this.store.selectSnapshot(StoryState.getStory).displayName;
      const message = 'You must save your changes to publish your model.';

      this.saveNewBookmarkDialog(title, bookmarkTitle, message)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((savedBookmark) => {
          if (savedBookmark) {
            this.previewBookmark(savedBookmark);
          }
        });
    }
  }

  isBookmarkEditable(bookmark: BookmarkModel) {
    if (!bookmark) {
      return false;
    } else {
      const { is_owner, group_access } = bookmark;
      return is_owner || (group_access ? group_access.some((access) => access.member_edit) : false);
    }
  }

  getBookmarkSaveType(bookmark: BookmarkModel): 'save' | 'saveAs' {
    if (!bookmark) {
      return 'saveAs';
    } else {
      return this.isBookmarkEditable(bookmark) ? 'save' : 'saveAs';
    }
  }

  handlePreviewClick() {
    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
    if (bookmark) {
    }    
  }

  handleSaveBookmarkSuccess(action: SaveBookmarkSuccess) {
    const { bookmark, story } = action;
    const { id, title, type } = bookmark;

    // set current route to updated content
    const query = `?id=${id}`;
    this.location.replaceState('edit', query);

    this.snackBar.open(`${title} saved to your dashboard.`);

    if (story) {
      const currentIdx = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterIndex);
      this.store.dispatch(new BuildStory(id, story, <StoryType>type, StoryLoadTypeEnum.OnDemand, currentIdx));
    }
  }

  handleSaveModuleSuccess(action: SaveModuleSuccess) {
    this.id = this.sanitizeRouteId(action.id);
  }

  handleSaveBookmarkFailure(action: SaveBookmarkFail) {
    this.snackBar.open(`Unable to save "${action.bookmarkEntry.title}". Please try again.`);
  }

  openBookmarkAccessDialog() {
    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
    if (bookmark) {
      this.dialogService.openBookmarkAccessDialog(bookmark);
    }    
  }

  openChapterContentDialog() {
    this.dialog.open(ContentDialogComponent, {
      width: '60vw',
      height: 'auto',
      data: {
        chapterId: this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId)
      }
    });
  }

  undo() {
    this.store.dispatch(new Undo());
  }

  redo() {
    this.store.dispatch(new Redo());
  }

  resetHistory() {
    this.store.dispatch(new ResetHistory());
    this.historyService.recordViewGraphHistory();
  }

  upgrade() {
    this.dialogService.openUpgradeDialog();
  }

  openInSceneEditor() {
    const url = `https://scene-editor.biodigital.com/apps/editor/?m=${this.id}.json`;
    window.open(url);
  }

  // Open current element in new window as widget (unsaved)
  openInWidget() {
    const root = window.location.origin;
    // tslint:disable-next-line:max-line-length
    const url = `${root}/widget/?m=${this.id}.json&ui-info=true&ui-load-progress=false&ui-fullscreen=false&ui-zoom=false&ui-center=false&ui-camera-mode=false&ui-share=false`;
    window.open(url);
  }

  // Open current element in new window as view (unsaved)
  openInView() {
    const root = window.location.origin;
    const url = `${root}/view?id=${this.id}`;
    window.open(url);
  }

  // Bookmark routes use appropriate ID parameters, but internal users would like the ability to paste
  // in legacy paths. This means stripping out the .json extension at time of page load and accounting
  // for that conversion later on. 
  private sanitizeRouteId(id: string): string {
    id = id.replace(/\.json$/, '');
    const query = `?id=${id}`;
    this.location.replaceState('edit', query);
    return id;
  }

  nextChapter() {
    if (this.store.selectSnapshot(StoryChaptersState.getAllChaptersReady)) {
      this.store.dispatch(new NextChapter());
    }
  }

  previousChapter() {
    if (this.store.selectSnapshot(StoryChaptersState.getAllChaptersReady)) {
      this.store.dispatch(new PreviousChapter());
    }
  }

  private confirmSaveDialog(canCancel?: boolean): Observable<boolean> {
    return this.dialogService.openConfirmDialog({
      data: {
        titleText: 'Save Changes',
        bodyText: 'Would you like to save your changes to your model?',
        confirmText: 'Save',
        cancelText: `Don't Save`,
        miscText: canCancel ? 'Cancel' : '',
        confirmColor: 'warn'
      }
    })
      .afterClosed();
  }

  private saveBookmark(bookmark: BookmarkModel, notify: boolean = false): Observable<BookmarkModel> {
    // notify user
    if (notify) {
      this.snackBar.open(`Saving "${bookmark.title}"...`);
    }

    // save + return after success/error
    return this.store.dispatch(new SaveBookmark({ isOwner: bookmark.is_owner, id: bookmark.id, title: bookmark.title }, true))
      .pipe(
        flatMap(() => {
          return this.actions
            .pipe(
              ofActionDispatched(SaveBookmarkSuccess, SaveBookmarkFail),
              first()
            );
        }),
        map((e) => {
          if (e instanceof SaveBookmarkSuccess) {
            return this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
          } else {
            return bookmark;
          }
        })
      );
  }

  private saveNewBookmarkDialog(title: string, bookmarkTitle: string, message?: string): Observable<BookmarkModel> {
    return this.dialogService
      .openSaveAsBookmarkDialog(title, bookmarkTitle, message)
      .afterClosed();
  }

  private previewBookmark(bookmark: BookmarkModel) {
    this.dialog.open(PublishDialogComponent, {
      width: '90vw',
      height: 'auto',
      data: <PublishDialogData>{
        bookmark: bookmark
      }
    });
  }
}
